<template>
  <div class="p-3 plan-tab-shell">
    <div class="card card-plain plan-tab-report-card">
      <div class="card-body py-4 plan-tab-report-body">
        <div v-if="isHistoryTeamTab" class="report-select-box plan-tab-toolbar">
          <p class="report-preview-title mb-2">Generated Reports</p>
          <div v-if="loadingHistory" class="text-xs text-secondary py-2">Loading history...</div>
          <div v-else-if="generatedReports.length === 0" class="text-xs text-secondary py-2">
            No generated reports yet.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Report Number</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Type</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Test Run</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Sub Project</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">PM</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Download</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in generatedReports" :key="row.reportId">
                  <td class="text-xs font-weight-bold">{{ row.reportNumber }}</td>
                  <td class="text-xs">{{ reportTypeLabelOf(row.reportType) }}</td>
                  <td class="text-xs">{{ row.testRunName || "—" }}</td>
                  <td class="text-xs">{{ row.subProjectName || "—" }}</td>
                  <td class="text-xs">{{ row.projectManager || "—" }}</td>
                  <td class="text-xs">{{ formatHistoryDateTime(row.createdAt) }}</td>
                  <td class="text-xs text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary mb-0"
                      :disabled="downloadingHistoryId === row.reportId"
                      @click="downloadHistoryPdf(row)"
                    >
                      <i class="fas fa-download me-1"></i>
                      {{ downloadingHistoryId === row.reportId ? "..." : "Download" }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else class="report-select-box plan-tab-toolbar">
          <div class="row g-2 align-items-end">
            <div class="col-12 col-md-4">
              <label class="text-xs font-weight-bold text-secondary mb-2 d-block">Report Type</label>
              <base-select
                v-model="selectedReportType"
                size="sm"
                :options="reportTypeOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select report type —"
              />
            </div>
            <div class="col-12 col-md-5">
              <label class="text-xs font-weight-bold text-secondary mb-2 d-block">Test Run</label>
              <base-select
                v-model="selectedRunId"
                size="sm"
                :options="testRunSelectOptions"
                option-label="label"
                option-value="value"
                :placeholder="loading ? 'Loading test runs...' : '— Select test run —'"
                :disabled="loading || filteredRuns.length === 0"
                @change="onRunChange"
              />
            </div>
            <div class="col-12 col-md-3">
              <button
                type="button"
                class="btn btn-sm bg-gradient-primary w-100 mb-0"
                :disabled="loading || downloadingPdf || !selectedRunId || !selectedReportType"
                @click="downloadPdf"
              >
                <i class="fas fa-download me-1"></i>
                {{ downloadingPdf ? "Generating..." : "Download PDF" }}
              </button>
            </div>
          </div>
        </div>

        <p v-if="!isHistoryTeamTab && !loading && filteredRuns.length === 0" class="text-xs text-secondary mt-2 mb-0 plan-tab-toolbar">
          {{ emptyRunsMessage }}
        </p>

        <div v-if="!isHistoryTeamTab" class="plan-tab-scroll-area">
        <div class="report-preview mt-4" v-if="isEditableReportType">
          <div class="report-editor-layout">
            <div class="report-editor-form">
              <p class="report-preview-title mb-2">{{
                selectedReportType === "test_report_with_test_case"
                  ? "Test Report with Test Case"
                  : selectedReportType === "test_report_for_vendor"
                    ? "Test Report For Vendor"
                    : "Test Report Example"
              }}</p>
              <div class="report-preview-card">
            <div class="pdf-like-header mb-3">
              <p class="pdf-like-title mb-1">TEST REPORT : {{ (reportDisplayProject === "—" ? "SUB PROJECT" : reportDisplayProject).toUpperCase() }}</p>
              <p class="pdf-like-sub mb-0">{{ selectedRunName }}</p>
            </div>

            <div class="pdf-like-info-grid mb-3">
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Sub Project</div>
                <div class="pdf-like-info-value">{{ reportDisplayProject }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Version</div>
                <div class="pdf-like-info-value">{{ reportForm.version || "—" }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Date</div>
                <div class="pdf-like-info-value">{{ reportDisplayDate }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Report Number</div>
                <div class="pdf-like-info-value">{{ reportForm.reportNumber || "—" }}</div>
              </div>
            </div>

            <div class="pdf-like-section-title mb-2">Report Details</div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-6">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">QA Owner</label>
                <div class="qa-owner-multiselect" ref="qaOwnerDropdownRef">
                  <div class="qa-owner-selector" @click="showQaOwnerDropdown = !showQaOwnerDropdown">
                    <span v-if="reportForm.qaOwnerIds.length === 0" class="text-secondary">— Select QA Owner —</span>
                    <span v-for="u in selectedQaOwners" :key="u.userId" class="qa-owner-chip">
                      {{ formatUserName(u) }}
                      <span class="qa-owner-chip-remove" @click.stop="toggleQaOwner(u.userId)">×</span>
                    </span>
                    <i class="fas fa-chevron-down ms-auto text-secondary" style="font-size:0.65rem;"></i>
                  </div>
                  <div v-if="showQaOwnerDropdown" class="qa-owner-dropdown-menu">
                    <div v-for="u in qaOwnerCandidates" :key="u.userId" class="qa-owner-dropdown-item" @mousedown.prevent="toggleQaOwner(u.userId)">
                      <input type="checkbox" class="form-check-input me-2" :checked="reportForm.qaOwnerIds.includes(u.userId)" />
                      <span>{{ formatUserName(u) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-3">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Date</label>
                <input v-model="reportForm.reportDate" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-12 col-md-3">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Version</label>
                <input v-model="reportForm.version" type="text" class="form-control form-control-sm" placeholder="Version" />
              </div>
            </div>

            <div class="status-quality-grid mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-0 sq-label-status">Status</label>
              <label class="text-xs font-weight-bold text-secondary mb-0 sq-label-quality">Overall Quality</label>
              <div class="sq-actions d-flex gap-1">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary mb-0 sq-gen-btn"
                  :disabled="!selectedRunId || generatingOverallQuality"
                  @click="generateOverallQuality('th')"
                >
                  <i class="fas fa-wand-magic-sparkles me-1"></i>
                  <span v-if="generatingOverallQuality && overallQualityLang === 'th'">กำลังสร้าง...</span>
                  <span v-else>Generate TH</span>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary mb-0 sq-gen-btn"
                  :disabled="!selectedRunId || generatingOverallQuality"
                  @click="generateOverallQuality('en')"
                >
                  <i class="fas fa-wand-magic-sparkles me-1"></i>
                  <span v-if="generatingOverallQuality && overallQualityLang === 'en'">Generating...</span>
                  <span v-else>Generate EN</span>
                </button>
              </div>
              <base-select
                v-model="reportForm.overallQualityStatus"
                size="sm"
                class="sq-status"
                :options="overallQualityStatusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select —"
              />
              <div class="sq-quality-wrap">
                <textarea
                  ref="overallQualityTextarea"
                  v-model="reportForm.overallQuality"
                  class="form-control form-control-sm sq-quality"
                  rows="2"
                  @input="resizeOverallQualityTextarea"
                />
                <p v-if="overallQualityAiError" class="text-xs text-danger mb-0 mt-1">{{ overallQualityAiError }}</p>
              </div>
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Scope of Testing</label>
              <textarea
                v-model="reportForm.scopeOfTesting"
                class="form-control form-control-sm bullet-textarea"
                rows="3"
                placeholder="Test scope (from Scope tab or generated from test run)"
              />
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-2 d-block">Function / Category</label>
              <div class="scenario-list">
                <div v-if="!selectedRunId" class="text-xs text-secondary">Please choose the Test Run.</div>
                <div v-else-if="runScenarios.length === 0" class="text-xs text-secondary">No scenario available for this test run.</div>
                <div v-else v-for="(s, idx) in runScenarios" :key="s.key || idx" class="scenario-row">
                  <div class="scenario-name">{{ s.name || "(No Scenario)" }}</div>
                  <textarea
                    v-model="scenarioConclusions[scenarioKey(s, idx)]"
                    class="form-control form-control-sm"
                    rows="2"
                    placeholder="Conclusion for this scenario"
                  />
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Critical &amp; High Priority Bugs</label>
              <textarea
                v-model="reportForm.criticalHighBugs"
                class="form-control form-control-sm bullet-textarea"
                rows="2"
                placeholder="One item per line (e.g. • BUG-123: description)"
              />
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Remark</label>
              <textarea
                v-model="reportForm.otherIssues"
                class="form-control form-control-sm bullet-textarea"
                rows="2"
                placeholder="One item per line — non-Critical/non-High issues (e.g. • Medium bug)"
              />
            </div>

            <div class="mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Artifacts &amp; Evidence</label>
              <textarea
                v-model="reportForm.artifactsEvidence"
                class="form-control form-control-sm bullet-textarea"
                rows="2"
                placeholder="One item per line (e.g. • Test report PDF, screenshots)"
              />
            </div>

            <div class="row g-2">
              <div :class="selectedQaOwners.length >= 2 ? 'col-12 col-md-4' : 'col-12 col-md-6'">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">QA Owner (Deliverable)</label>
                <base-select
                  v-model="reportForm.deliverableQaOwnerId"
                  size="sm"
                  :options="deliverableQaOwnerOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— Select QA Owner —"
                />
              </div>
              <div v-if="selectedQaOwners.length >= 2" class="col-12 col-md-4">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">QA Owner (Deliverable) 2</label>
                <base-select
                  v-model="reportForm.deliverableQaOwnerId2"
                  size="sm"
                  :options="deliverableQaOwnerOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— Select QA Owner —"
                />
              </div>
              <div :class="selectedQaOwners.length >= 2 ? 'col-12 col-md-4' : 'col-12 col-md-6'">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Project Manager</label>
                <input v-model="reportForm.projectManager" type="text" class="form-control form-control-sm" />
              </div>
            </div>

            <template v-if="selectedReportType === 'test_report_with_test_case'">
              <div class="pdf-like-section-title mt-4 mb-2">Test Cases</div>
              <div v-if="loadingRunResult" class="text-xs text-secondary py-3 text-center">
                <i class="fas fa-spinner fa-spin me-1"></i> Loading results...
              </div>
              <template v-else-if="runResultStats">
                <div class="pdf-like-info-grid pdf-like-info-grid--stats mb-3">
                  <div class="pdf-like-info-item">
                    <div class="pdf-like-info-label">Total</div>
                    <div class="pdf-like-info-value">{{ runResultStats.total }}</div>
                  </div>
                  <div class="pdf-like-info-item">
                    <div class="pdf-like-info-label">Passed</div>
                    <div class="pdf-like-info-value" :style="{ color: viewResultColor('Pass') }">{{ runResultStats.passed }}</div>
                  </div>
                  <div class="pdf-like-info-item">
                    <div class="pdf-like-info-label">Failed</div>
                    <div class="pdf-like-info-value" :style="{ color: viewResultColor('Fail') }">{{ runResultStats.failed }}</div>
                  </div>
                  <div class="pdf-like-info-item">
                    <div class="pdf-like-info-label">No Run</div>
                    <div class="pdf-like-info-value" :style="{ color: viewResultColor('No Run') }">{{ runResultStats.noRun || 0 }}</div>
                  </div>
                  <div class="pdf-like-info-item">
                    <div class="pdf-like-info-label">Not Run</div>
                    <div class="pdf-like-info-value" :style="{ color: viewResultColor('Not Run') }">{{ runResultStats.notRun || 0 }}</div>
                  </div>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">ID</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Test Case</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in runResultRows" :key="row.testRunDetailId">
                        <td class="text-xs">{{ formatRunCaseId(row) }}</td>
                        <td class="text-xs">{{ row.title || "—" }}</td>
                        <td class="text-xs text-center run-result-status">
                          <strong :style="{ color: viewResultColor(row.status) }">{{ displayRunResult(row.status) }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
              <p v-else class="text-xs text-secondary mb-0">Please choose a Test Run.</p>
            </template>
              </div>
            </div>
          </div>
        </div>

        <div class="report-preview mt-4" v-else-if="selectedReportType === 'test_run_result'">
          <p class="report-preview-title mb-2">Test Run Result (Status)</p>
          <div class="report-preview-card">
            <div class="pdf-like-header mb-3">
              <p class="pdf-like-title mb-1">TEST RUN RESULT (STATUS) : {{ (reportDisplayProject === "—" ? "SUB PROJECT" : reportDisplayProject).toUpperCase() }}</p>
              <p class="pdf-like-sub mb-0">{{ selectedRunName }}</p>
            </div>
            <div class="pdf-like-info-grid mb-3">
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Sub Project</div>
                <div class="pdf-like-info-value">{{ reportDisplayProject }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Version</div>
                <div class="pdf-like-info-value">{{ reportForm.version || "—" }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Date</div>
                <div class="pdf-like-info-value">{{ reportDisplayDate }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Report Number</div>
                <div class="pdf-like-info-value">{{ reportForm.reportNumber || "—" }}</div>
              </div>
            </div>
            <div class="pdf-like-section-title mb-2">Report Details</div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-6">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">QA Owner</label>
                <div class="qa-owner-multiselect">
                  <div class="qa-owner-selector" @click="showQaOwnerDropdown = !showQaOwnerDropdown">
                    <span v-if="reportForm.qaOwnerIds.length === 0" class="text-secondary">— Select QA Owner —</span>
                    <span v-for="u in selectedQaOwners" :key="u.userId" class="qa-owner-chip">
                      {{ formatUserName(u) }}
                      <span class="qa-owner-chip-remove" @click.stop="toggleQaOwner(u.userId)">×</span>
                    </span>
                    <i class="fas fa-chevron-down ms-auto text-secondary" style="font-size:0.65rem;"></i>
                  </div>
                  <div v-if="showQaOwnerDropdown" class="qa-owner-dropdown-menu">
                    <div v-for="u in qaOwnerCandidates" :key="u.userId" class="qa-owner-dropdown-item" @mousedown.prevent="toggleQaOwner(u.userId)">
                      <input type="checkbox" class="form-check-input me-2" :checked="reportForm.qaOwnerIds.includes(u.userId)" />
                      <span>{{ formatUserName(u) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-3">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Date</label>
                <input v-model="reportForm.reportDate" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-12 col-md-3">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Version</label>
                <input v-model="reportForm.version" type="text" class="form-control form-control-sm" placeholder="Version" />
              </div>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-6">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Project Manager</label>
                <input v-model="reportForm.projectManager" type="text" class="form-control form-control-sm" />
              </div>
            </div>
            <div v-if="loadingRunResult" class="text-xs text-secondary py-3 text-center">
              <i class="fas fa-spinner fa-spin me-1"></i> Loading results...
            </div>
            <template v-else-if="runResultStats">
              <div class="status-quality-grid mb-3">
                <label class="text-xs font-weight-bold text-secondary mb-0 sq-label-status">Status</label>
                <label class="text-xs font-weight-bold text-secondary mb-0 sq-label-quality">Overall Quality</label>
                <div class="sq-actions d-flex gap-1">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary mb-0 sq-gen-btn"
                    :disabled="!selectedRunId || generatingOverallQuality"
                    @click="generateOverallQuality('th')"
                  >
                    <i class="fas fa-wand-magic-sparkles me-1"></i>
                    <span v-if="generatingOverallQuality && overallQualityLang === 'th'">กำลังสร้าง...</span>
                    <span v-else>Generate TH</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary mb-0 sq-gen-btn"
                    :disabled="!selectedRunId || generatingOverallQuality"
                    @click="generateOverallQuality('en')"
                  >
                    <i class="fas fa-wand-magic-sparkles me-1"></i>
                    <span v-if="generatingOverallQuality && overallQualityLang === 'en'">Generating...</span>
                    <span v-else>Generate EN</span>
                  </button>
                </div>
                <base-select
                  v-model="reportForm.overallQualityStatus"
                  size="sm"
                  class="sq-status"
                  :options="overallQualityStatusOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— Select —"
                />
                <div class="sq-quality-wrap">
                  <textarea
                    ref="overallQualityTextarea"
                    v-model="reportForm.overallQuality"
                    class="form-control form-control-sm sq-quality"
                    rows="2"
                    @input="resizeOverallQualityTextarea"
                  />
                  <p v-if="overallQualityAiError" class="text-xs text-danger mb-0 mt-1">{{ overallQualityAiError }}</p>
                </div>
              </div>
              <div class="pdf-like-info-grid pdf-like-info-grid--stats mb-3">
                <div class="pdf-like-info-item">
                  <div class="pdf-like-info-label">Total</div>
                  <div class="pdf-like-info-value">{{ runResultStats.total }}</div>
                </div>
                <div class="pdf-like-info-item">
                  <div class="pdf-like-info-label">Passed</div>
                  <div class="pdf-like-info-value" :style="{ color: viewResultColor('Pass') }">{{ runResultStats.passed }}</div>
                </div>
                <div class="pdf-like-info-item">
                  <div class="pdf-like-info-label">Failed</div>
                  <div class="pdf-like-info-value" :style="{ color: viewResultColor('Fail') }">{{ runResultStats.failed }}</div>
                </div>
                <div class="pdf-like-info-item">
                  <div class="pdf-like-info-label">No Run</div>
                  <div class="pdf-like-info-value" :style="{ color: viewResultColor('No Run') }">{{ runResultStats.noRun || 0 }}</div>
                </div>
                <div class="pdf-like-info-item">
                  <div class="pdf-like-info-label">Not Run</div>
                  <div class="pdf-like-info-value" :style="{ color: viewResultColor('Not Run') }">{{ runResultStats.notRun || 0 }}</div>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-sm align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">ID</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Test Case</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in runResultRows" :key="row.testRunDetailId">
                      <td class="text-xs">{{ formatRunCaseId(row) }}</td>
                      <td class="text-xs">{{ row.title || "—" }}</td>
                      <td class="text-xs text-center run-result-status">
                        <strong :style="{ color: viewResultColor(row.status) }">{{ displayRunResult(row.status) }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <p v-else class="text-xs text-secondary mb-0">Please choose a Test Run.</p>
          </div>
        </div>

        <div class="report-preview mt-4" v-else-if="selectedReportType === 'test_run_result_detail'">
          <p class="report-preview-title mb-2">Test Run Result (Details)</p>
          <div class="report-preview-card qa-tr-report-detail">
            <div class="pdf-like-header mb-3">
              <p class="pdf-like-title mb-1">TEST RUN RESULT (DETAILS) : {{ (reportDisplayProject === "—" ? "SUB PROJECT" : reportDisplayProject).toUpperCase() }}</p>
              <p class="pdf-like-sub mb-0">{{ selectedRunName }}</p>
            </div>
            <div class="pdf-like-info-grid mb-3">
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Sub Project</div>
                <div class="pdf-like-info-value">{{ reportDisplayProject }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Version</div>
                <div class="pdf-like-info-value">{{ reportForm.version || "—" }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Date</div>
                <div class="pdf-like-info-value">{{ reportDisplayDate }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Report Number</div>
                <div class="pdf-like-info-value">{{ reportForm.reportNumber || "—" }}</div>
              </div>
            </div>
            <div class="pdf-like-section-title mb-2">Report Details</div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-6">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">QA Owner</label>
                <div class="qa-owner-multiselect">
                  <div class="qa-owner-selector" @click="showQaOwnerDropdown = !showQaOwnerDropdown">
                    <span v-if="reportForm.qaOwnerIds.length === 0" class="text-secondary">— Select QA Owner —</span>
                    <span v-for="u in selectedQaOwners" :key="u.userId" class="qa-owner-chip">
                      {{ formatUserName(u) }}
                      <span class="qa-owner-chip-remove" @click.stop="toggleQaOwner(u.userId)">×</span>
                    </span>
                    <i class="fas fa-chevron-down ms-auto text-secondary" style="font-size:0.65rem;"></i>
                  </div>
                  <div v-if="showQaOwnerDropdown" class="qa-owner-dropdown-menu">
                    <div v-for="u in qaOwnerCandidates" :key="u.userId" class="qa-owner-dropdown-item" @mousedown.prevent="toggleQaOwner(u.userId)">
                      <input type="checkbox" class="form-check-input me-2" :checked="reportForm.qaOwnerIds.includes(u.userId)" />
                      <span>{{ formatUserName(u) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-3">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Date</label>
                <input v-model="reportForm.reportDate" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-12 col-md-3">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Version</label>
                <input v-model="reportForm.version" type="text" class="form-control form-control-sm" placeholder="Version" />
              </div>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-6">
                <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Project Manager</label>
                <input v-model="reportForm.projectManager" type="text" class="form-control form-control-sm" />
              </div>
            </div>
            <div class="status-quality-grid mb-3">
              <label class="text-xs font-weight-bold text-secondary mb-0 sq-label-status">Status</label>
              <label class="text-xs font-weight-bold text-secondary mb-0 sq-label-quality">Overall Quality</label>
              <div class="sq-actions d-flex gap-1">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary mb-0 sq-gen-btn"
                  :disabled="!selectedRunId || generatingOverallQuality"
                  @click="generateOverallQuality('th')"
                >
                  <i class="fas fa-wand-magic-sparkles me-1"></i>
                  <span v-if="generatingOverallQuality && overallQualityLang === 'th'">กำลังสร้าง...</span>
                  <span v-else>Generate TH</span>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary mb-0 sq-gen-btn"
                  :disabled="!selectedRunId || generatingOverallQuality"
                  @click="generateOverallQuality('en')"
                >
                  <i class="fas fa-wand-magic-sparkles me-1"></i>
                  <span v-if="generatingOverallQuality && overallQualityLang === 'en'">Generating...</span>
                  <span v-else>Generate EN</span>
                </button>
              </div>
              <base-select
                v-model="reportForm.overallQualityStatus"
                size="sm"
                class="sq-status"
                :options="overallQualityStatusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select —"
              />
              <div class="sq-quality-wrap">
                <textarea
                  ref="overallQualityTextarea"
                  v-model="reportForm.overallQuality"
                  class="form-control form-control-sm sq-quality"
                  rows="2"
                  @input="resizeOverallQualityTextarea"
                />
                <p v-if="overallQualityAiError" class="text-xs text-danger mb-0 mt-1">{{ overallQualityAiError }}</p>
              </div>
            </div>
            <div v-if="runResultStats" class="pdf-like-info-grid pdf-like-info-grid--stats mb-3">
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Total</div>
                <div class="pdf-like-info-value">{{ runResultStats.total }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Passed</div>
                <div class="pdf-like-info-value" :style="{ color: viewResultColor('Pass') }">{{ runResultStats.passed }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Failed</div>
                <div class="pdf-like-info-value" :style="{ color: viewResultColor('Fail') }">{{ runResultStats.failed }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">No Run</div>
                <div class="pdf-like-info-value" :style="{ color: viewResultColor('No Run') }">{{ runResultStats.noRun || 0 }}</div>
              </div>
              <div class="pdf-like-info-item">
                <div class="pdf-like-info-label">Not Run</div>
                <div class="pdf-like-info-value" :style="{ color: viewResultColor('Not Run') }">{{ runResultStats.notRun || 0 }}</div>
              </div>
            </div>
            <div v-if="loadingRunDetail" class="text-xs text-secondary py-3 text-center">
              <i class="fas fa-spinner fa-spin me-1"></i> Loading details...
            </div>
            <p v-else-if="!selectedRunId" class="text-xs text-secondary mb-0">Please choose a Test Run.</p>
            <p v-else-if="!runDetailCases.length" class="text-xs text-secondary mb-0">No test cases in this run.</p>
            <article v-for="tc in runDetailCases" :key="tc.key" class="qa-tr-report-case">
              <div class="qa-tr-report-case-top">
                <div>
                  <p class="qa-tr-report-case-id mb-0">{{ tc.tcLabel }}</p>
                  <h6 class="qa-tr-report-case-name mb-1">{{ tc.title || "Untitled" }}</h6>
                  <div class="qa-tr-report-type-row">
                    <span><strong>Type:</strong> {{ enumLabel(tc.testType) }}</span>
                    <span><strong>Priority:</strong> {{ enumLabel(tc.priority) }}</span>
                  </div>
                  <div class="qa-tr-report-hierarchy">
                    <div><strong>Function:</strong> {{ tc.function || "—" }}</div>
                    <div><strong>Scenario:</strong> {{ tc.scenario || "—" }}</div>
                    <div><strong>Sub-Scenario:</strong> {{ tc.subScenario || "—" }}</div>
                  </div>
                </div>
                <div class="qa-tr-report-result-head">
                  <strong>Result:</strong>
                  <span class="qa-tr-report-result-chip" :style="{ color: viewResultColor(tc.runStatus), borderColor: viewResultColor(tc.runStatus) }">
                    {{ displayRunResult(tc.runStatus) }}
                  </span>
                </div>
              </div>
              <div class="qa-tr-report-fields">
                <div>
                  <p class="qa-tr-report-label">Pre-Condition</p>
                  <BulletText :text="tc.preconditions" list-class="mb-0" fallback="—" />
                </div>
                <div>
                  <p class="qa-tr-report-label">Test Data</p>
                  <BulletText :text="tc.testData" list-class="mb-0" fallback="—" />
                </div>
              </div>
              <table class="qa-tr-report-steps">
                <colgroup>
                  <col class="qa-tr-report-col-no" />
                  <col class="qa-tr-report-col-module" />
                  <col class="qa-tr-report-col-step" />
                  <col class="qa-tr-report-col-expected" />
                  <col class="qa-tr-report-col-actual" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Module</th>
                    <th>Test Step</th>
                    <th>Expected Result</th>
                    <th>Actual Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!(tc.steps || []).length">
                    <td class="text-secondary">—</td>
                    <td class="text-secondary">—</td>
                    <td class="text-secondary">—</td>
                    <td class="text-secondary">—</td>
                    <td><BulletText :text="viewOverviewActual(tc)" list-class="mb-0" fallback="—" /></td>
                  </tr>
                  <tr v-for="(step, idx) in tc.steps" :key="`${tc.key}-std-${idx}`">
                    <td>{{ step.stepNo || idx + 1 }}</td>
                    <td><BulletText :text="step.platform" list-class="mb-0" fallback="—" /></td>
                    <td><BulletText :text="step.stepDetail" list-class="mb-0" fallback="—" /></td>
                    <td><BulletText :text="step.expectedResult" list-class="mb-0" fallback="—" /></td>
                    <td v-if="viewActualIsByStep(tc) || idx === 0" :rowspan="viewActualIsByStep(tc) ? 1 : tc.steps.length">
                      <BulletText :text="viewActualText(tc, idx)" list-class="mb-0" fallback="—" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </article>
          </div>
        </div>

        <div class="report-preview mt-4" v-else>
          <p class="report-preview-title mb-2">Report Preview</p>
          <div class="report-preview-card">
            <p class="text-sm text-secondary mb-0 report-empty-message">Please choose the Report Type and Test Run.</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import BulletText from "@/components/BulletText.vue";
import { API_BASE_URL, fetchWithTimeout } from "@/config/apiBase";
import { aiApi, projectPlansApi, reportsApi, testRunDetailsApi, testRunsApi, usersApi } from "@/services/backendApi";
import { isPlanHistoryStatus } from "@/utils/subProjectStatus";
import { normalizeTeamCode, userRoleNames } from "@/utils/userTeams";

function isByStepMode(mode) {
  const m = String(mode || "").trim().toLowerCase();
  return m === "step" || m === "bystep";
}

const PDF_FONT_FAMILY = "K2D";
let k2dFontVfsCache = null;
let k2dCanvasFontsReady = null;

export default {
  name: "TestReportTab",
  components: { BulletText },
  props: {
    standalone: { type: Boolean, default: false },
    teamFilter: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      loadingRunResult: false,
      loadingHistory: false,
      downloadingPdf: false,
      downloadingHistoryId: null,
      generatedReports: [],
      generatingOverallQuality: false,
      overallQualityLang: "",
      overallQualityAiError: "",
      runs: [],
      selectedRunId: "",
      selectedReportType: "",
      selectedRunDetail: null,
      users: [],
      runScenarios: [],
      runResultStats: null,
      runResultRows: [],
      loadingRunDetail: false,
      runDetailCases: [],
      showQaOwnerDropdown: false,
      scenarioConclusions: {},
      projectPlanPmName: "",
      subProjectId: null,
      subProjectScopeText: "",
      reportForm: {
        qaOwnerIds: [],
        reportDate: "",
        version: "",
        projectName: "",
        reportNumber: "",
        overallQualityStatus: "",
        overallQuality: "",
        scopeOfTesting: "",
        criticalHighBugs: "",
        otherIssues: "",
        artifactsEvidence: "",
        deliverableQaOwnerId: "",
        deliverableQaOwnerId2: "",
        qaLead: "",
        projectManager: "",
      },
    };
  },
  async created() {
    document.addEventListener("click", this.closeQaOwnerDropdown);
    if (this.isHistoryTeamTab) {
      await this.loadGeneratedReports();
    } else {
      await Promise.all([this.loadRuns(), this.loadUsers(), this.loadProjectPlan(), this.loadNextReportNumber()]);
    }
    this.$nextTick(() => this.resizeOverallQualityTextarea());
  },
  mounted() {
    this.$nextTick(() => this.resizeOverallQualityTextarea());
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeQaOwnerDropdown);
  },
  computed: {
    ...mapState(["user"]),
    isEditableReportType() {
      return ["test_report", "test_report_with_test_case", "test_report_for_vendor"].includes(
        this.selectedReportType
      );
    },
    currentUserId() {
      return Number(this.user?.userId || this.user?.id) || 0;
    },
    qaOwnerCandidates() {
      const list = (this.users || []).filter((u) => this.isQaPerson(u));
      return list.length ? list : (this.users || []);
    },
    selectedRun() {
      return this.selectedRunDetail || this.runs.find((r) => String(r.id) === String(this.selectedRunId)) || null;
    },
    selectedRunName() {
      return this.selectedRun?.name || "—";
    },
    reportDisplayProject() {
      const name = String(
        this.selectedRun?.subProjectName || this.reportForm.projectName || ""
      ).trim();
      return name || "—";
    },
    reportDisplayDate() {
      return this.formatReportPdfDate(this.reportForm.reportDate) || this.reportForm.reportDate || "—";
    },
    reportTypeLabel() {
      const map = {
        test_report: "Test Report",
        test_run_result: "Test Run Result (Status)",
        test_run_result_detail: "Test Run Result (Details)",
        test_report_with_test_case: "Test Report with Test Case",
        test_report_for_vendor: "Test Report For Vendor",
      };
      return map[this.selectedReportType] || "Test Report";
    },
    selectedQaOwners() {
      return this.users.filter((u) => this.reportForm.qaOwnerIds.includes(u.userId));
    },
    reportTypeOptions() {
      return [
        { label: "Test Report", value: "test_report" },
        { label: "Test Run Result (Status)", value: "test_run_result" },
        { label: "Test Run Result (Details)", value: "test_run_result_detail" },
        { label: "Test Report with Test Case", value: "test_report_with_test_case" },
        { label: "Test Report For Vendor", value: "test_report_for_vendor" },
      ];
    },
    isHistoryTeamTab() {
      return this.standalone && String(this.teamFilter || "").trim().toLowerCase() === "history";
    },
    activeTeamCode() {
      if (!this.standalone || this.isHistoryTeamTab) return "";
      const key = String(this.teamFilter || "").trim().toLowerCase();
      if (key === "unassign") return "c";
      return normalizeTeamCode(key);
    },
    emptyRunsMessage() {
      if (this.standalone && this.isHistoryTeamTab) return "No generated reports yet.";
      if (this.standalone) return "No test run available for this team.";
      return "No test run available for this plan.";
    },
    filteredRuns() {
      let list = this.runs || [];
      if (this.standalone && this.teamFilter) {
        if (this.isHistoryTeamTab) {
          list = list.filter((r) => this.isHistoryRun(r));
        } else {
          const code = this.activeTeamCode;
          list = list.filter((r) => {
            if (this.isHistoryRun(r)) return false;
            return this.teamCodeOf(r.teamTrimmed || r.team) === code;
          });
        }
      }
      return list;
    },
    resolvedPlanId() {
      if (!this.standalone) return this.$route.params.id;
      return this.selectedRun?.testPlanId || this.selectedRun?.projectPlanId || "";
    },
    testRunSelectOptions() {
      return (this.filteredRuns || []).map((run) => {
        const sub = String(run.subProjectName || "").trim();
        const plan = String(run.testPlanName || run.planTaskName || "").trim();
        const prefix = [sub, plan].filter(Boolean).join(" / ");
        return {
          label: this.standalone && prefix ? `${prefix} — ${run.name}` : run.name,
          value: String(run.id),
        };
      });
    },
    overallQualityStatusOptions() {
      return [
        { label: "PASSED", value: "PASSED" },
        { label: "FAILED", value: "FAILED" },
        { label: "NO RUN", value: "NO RUN" },
        { label: "PASS WITH CONDITION", value: "PASS WITH CONDITION" },
      ];
    },
    deliverableQaOwnerOptions() {
      return (this.selectedQaOwners || []).map((u) => ({
        label: this.formatDeliverableName(u),
        value: u.userId,
      }));
    },
  },
  watch: {
    selectedRunId: {
      immediate: true,
      async handler() {
        await this.applyRunDefaults();
        if (this.standalone) await this.loadProjectPlan();
      },
    },
    async teamFilter() {
      if (!this.standalone) return;
      if (this.isHistoryTeamTab) {
        await this.loadGeneratedReports();
        return;
      }
      await Promise.all([
        this.runs.length ? Promise.resolve() : this.loadRuns(),
        this.users.length ? Promise.resolve() : this.loadUsers(),
        this.loadNextReportNumber(),
      ]);
      const ids = new Set((this.filteredRuns || []).map((r) => String(r.id)));
      if (this.selectedRunId && !ids.has(String(this.selectedRunId))) {
        this.selectedRunId = "";
        this.selectedRunDetail = null;
      }
    },
    selectedReportType(type) {
      if (type === "test_run_result" || type === "test_report_with_test_case" || type === "test_run_result_detail") {
        this.loadRunResultPreview();
      }
      if (type === "test_run_result_detail") {
        this.loadRunDetailPreview();
      }
    },
    "reportForm.qaOwnerIds": {
      handler() {
        this.syncDeliverableQaDefaults();
      },
      deep: true,
    },
    "reportForm.overallQuality"() {
      this.$nextTick(() => this.resizeOverallQualityTextarea());
    },
  },
  methods: {
    normalizeAssignRunIds(assignRun) {
      if (Array.isArray(assignRun)) return assignRun.map(Number).filter((id) => id > 0);
      if (assignRun) {
        const id = Number(assignRun);
        return id > 0 ? [id] : [];
      }
      return [];
    },
    syncDeliverableQaDefaults() {
      const ids = this.reportForm.qaOwnerIds.map(Number).filter((id) => id > 0);
      if (!ids.length) {
        this.reportForm.deliverableQaOwnerId = "";
        this.reportForm.deliverableQaOwnerId2 = "";
        return;
      }
      const d1 = Number(this.reportForm.deliverableQaOwnerId);
      const d2 = Number(this.reportForm.deliverableQaOwnerId2);
      if (!ids.includes(d1)) this.reportForm.deliverableQaOwnerId = ids[0];
      if (ids.length >= 2) {
        if (!ids.includes(d2) || d2 === Number(this.reportForm.deliverableQaOwnerId)) {
          const second = ids.find((id) => id !== Number(this.reportForm.deliverableQaOwnerId)) || ids[1];
          this.reportForm.deliverableQaOwnerId2 = second;
        }
      } else {
        this.reportForm.deliverableQaOwnerId2 = "";
      }
    },
    async applyRunDefaults() {
      const today = new Date();
      if (!this.reportForm.reportDate) this.reportForm.reportDate = today.toISOString().substring(0, 10);
      if (this.selectedRunId) {
        await Promise.all([this.loadSelectedRunDetail(), this.loadRunScenarios()]);
        if (
          this.selectedReportType === "test_run_result" ||
          this.selectedReportType === "test_report_with_test_case" ||
          this.selectedReportType === "test_run_result_detail"
        ) {
          await this.loadRunResultPreview();
        }
        if (this.selectedReportType === "test_run_result_detail") {
          await this.loadRunDetailPreview();
        }
      } else {
        this.selectedRunDetail = null;
        this.runScenarios = [];
        this.runResultStats = null;
        this.runResultRows = [];
        this.runDetailCases = [];
      }
      this.ensureDefaultQaOwner();
      this.syncDeliverableQaDefaults();
      if (this.projectPlanPmName) this.reportForm.projectManager = this.stripNicknameFromDisplay(this.projectPlanPmName);
      this.reportForm.version = this.selectedRun?.version || this.reportForm.version || "";
      this.reportForm.projectName = this.selectedRun?.subProjectName || this.reportForm.projectName || "";
      if (!this.reportForm.reportNumber) await this.loadNextReportNumber();
    },
    async onRunChange() {
      await this.applyRunDefaults();
    },
    async loadNextReportNumber() {
      try {
        const data = await reportsApi.nextGeneratedReportNumber();
        if (data?.reportNumber) this.reportForm.reportNumber = data.reportNumber;
      } catch (e) {
        console.error("load next report number error:", e);
        if (!this.reportForm.reportNumber) {
          const year = new Date().getFullYear();
          this.reportForm.reportNumber = `LN${year}-0001`;
        }
      }
    },
    reportTypeLabelOf(type) {
      const map = {
        test_report: "Test Report",
        test_run_result: "Test Run Result (Status)",
        test_run_result_detail: "Test Run Result (Details)",
        test_report_with_test_case: "Test Report with Test Case",
        test_report_for_vendor: "Test Report For Vendor",
      };
      return map[type] || type || "Test Report";
    },
    formatHistoryDateTime(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return d.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async loadGeneratedReports() {
      this.loadingHistory = true;
      try {
        const rows = await reportsApi.listGeneratedReports();
        this.generatedReports = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("load generated reports error:", e);
        this.generatedReports = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || "");
          resolve(result.includes(",") ? result.split(",")[1] : result);
        };
        reader.onerror = () => reject(new Error("Failed to read PDF"));
        reader.readAsDataURL(blob);
      });
    },
    triggerBlobDownload(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    async saveGeneratedReport(blob, fileName) {
      const pdfBase64 = await this.blobToBase64(blob);
      const saved = await reportsApi.saveGeneratedReport({
        reportType: this.selectedReportType,
        reportNumber: this.reportForm.reportNumber,
        testRunId: this.selectedRunId,
        owningTeam: this.selectedRun?.teamTrimmed || this.selectedRun?.team || this.selectedRun?.owningTeam || "",
        subProjectName: this.reportDisplayProject === "—" ? "" : this.reportDisplayProject,
        testRunName: this.selectedRunName === "—" ? "" : this.selectedRunName,
        projectManager: this.reportForm.projectManager || "",
        fileName,
        pdfBase64,
      });
      if (saved?.reportNumber) this.reportForm.reportNumber = saved.reportNumber;
      return saved;
    },
    async downloadHistoryPdf(row) {
      if (!row?.reportId) return;
      this.downloadingHistoryId = row.reportId;
      try {
        const { blob, fileName } = await reportsApi.downloadGeneratedReportPdf(row.reportId);
        this.triggerBlobDownload(blob, fileName || `${row.reportNumber || "report"}.pdf`);
      } catch (e) {
        console.error("download history pdf error:", e);
        window.alert(e?.message || "Failed to download report.");
      } finally {
        this.downloadingHistoryId = null;
      }
    },
    formatUserName(u) {
      const first = String(u?.firstName || "").trim();
      const last = String(u?.lastName || "").trim();
      return [first, last].filter(Boolean).join(" ") || "—";
    },
    stripNicknameFromDisplay(name) {
      const nick = String(name || "").trim();
      if (!nick) return "";
      return nick.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();
    },
    formatDeliverableName(u) {
      const first = this.stripNicknameFromDisplay(u?.firstName || "");
      const last = String(u?.lastName || "").trim();
      return [first, last].filter(Boolean).join(" ") || "—";
    },
    resizeOverallQualityTextarea() {
      const el = this.$refs.overallQualityTextarea;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.max(el.scrollHeight, 56)}px`;
    },
    scenarioKey(s, idx) {
      return String(s.key || s.scenarioId || s.id || idx);
    },
    closeQaOwnerDropdown(e) {
      const wrap = this.$refs.qaOwnerDropdownRef;
      if (wrap && typeof wrap.contains === "function" && !wrap.contains(e.target)) {
        this.showQaOwnerDropdown = false;
      }
    },
    toggleQaOwner(userId) {
      const id = Number(userId);
      const idx = this.reportForm.qaOwnerIds.indexOf(id);
      if (idx >= 0) this.reportForm.qaOwnerIds.splice(idx, 1);
      else this.reportForm.qaOwnerIds.push(id);
    },
    teamCodeOf(raw) {
      const code = normalizeTeamCode(raw);
      if (code === "a" || code === "b" || code === "c") return code;
      return "c";
    },
    isHistoryRun(run) {
      return isPlanHistoryStatus(run?.planStatus) || isPlanHistoryStatus(run?.status);
    },
    async loadRuns() {
      this.loading = true;
      try {
        const rows = await testRunsApi.getByProject(this.standalone ? undefined : this.$route.params.id);
        this.runs = Array.isArray(rows) ? rows : [];
      } catch (e) {
        console.error("load test runs for report error:", e);
        this.runs = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSelectedRunDetail() {
      try {
        const row = await testRunsApi.getById(this.selectedRunId);
        if (row && typeof row === "object") {
          this.selectedRunDetail = {
            ...row,
            id: row.id ?? row.testRunId ?? this.selectedRunId,
            name: row.name ?? row.testRunName ?? "",
            version: row.version ?? "",
            subProjectName: row.subProjectName ?? "",
            projectName: row.projectName ?? "",
            testPlanName: row.testPlanName ?? row.planTaskName ?? "",
          };
        }
      } catch (e) {
        console.error("load selected run detail error:", e);
        this.selectedRunDetail = null;
      }
    },
    isQaPerson(u) {
      const names = userRoleNames(u);
      if (!names.length) return false;
      return names.some((n) => n === "qa" || n.startsWith("qa_") || n.includes("qa"));
    },
    ensureDefaultQaOwner() {
      const me = this.currentUserId;
      if (!me) return;
      const ids = (this.reportForm.qaOwnerIds || []).map(Number).filter((id) => id > 0);
      if (ids.includes(me)) return;
      this.reportForm.qaOwnerIds = [me, ...ids];
    },
    async loadUsers() {
      try {
        const rows = await usersApi.getAll();
        this.users = (Array.isArray(rows) ? rows : []).map((u) => ({ ...u, userId: Number(u.userId) }));
        this.ensureDefaultQaOwner();
        this.syncDeliverableQaDefaults();
      } catch (e) {
        console.error("load users for report error:", e);
        this.users = [];
      }
    },
    async loadRunScenarios() {
      if (!this.selectedRunId) {
        this.runScenarios = [];
        return;
      }
      try {
        const rows = await testRunDetailsApi.getByRun(this.selectedRunId);
        const details = Array.isArray(rows) ? rows : [];
        const names = [...new Set(details.map((d) => String(d.scenario || "").trim()).filter(Boolean))];
        this.runScenarios = names.map((name, idx) => ({ key: `${idx}-${name}`, name }));
      } catch (e) {
        console.error("load run scenarios for report error:", e);
        this.runScenarios = [];
      }
    },
    async loadProjectPlan() {
      const planId = this.resolvedPlanId;
      if (!planId) {
        if (this.standalone) {
          this.reportForm.projectName = this.selectedRun?.subProjectName || "";
          this.subProjectId = null;
          this.subProjectScopeText = "";
        }
        return;
      }
      try {
        const plan = await projectPlansApi.getById(planId);
        this.reportForm.projectName = plan?.subProjectName || "";
        this.subProjectId = plan?.subProjectId || null;
        const pmName = this.stripNicknameFromDisplay(plan?.projectOwnerName || "");
        this.projectPlanPmName = pmName;
        if (pmName) this.reportForm.projectManager = pmName;
        await this.loadSubProjectScope();
      } catch (e) {
        console.error("load project plan for report error:", e);
      }
    },
    async loadSubProjectScope() {
      if (!this.subProjectId) {
        this.subProjectScopeText = "";
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope`);
        const data = await res.json();
        const scope = String(data?.scope || "").trim();
        this.subProjectScopeText = scope;
        if (scope && !String(this.reportForm.scopeOfTesting || "").trim()) {
          this.reportForm.scopeOfTesting = scope;
        }
      } catch (e) {
        console.error("load sub-project scope for report error:", e);
        this.subProjectScopeText = "";
      }
    },
    async loadRunResultPreview() {
      const needsRunResult =
        this.selectedReportType === "test_run_result" ||
        this.selectedReportType === "test_run_result_detail" ||
        this.selectedReportType === "test_report_with_test_case";
      if (!this.selectedRunId || !needsRunResult) {
        this.runResultStats = null;
        this.runResultRows = [];
        return;
      }
      this.loadingRunResult = true;
      try {
        const data = await reportsApi.getTestReport(this.selectedRunId);
        this.runResultStats = data?.stats || null;
        this.runResultRows = Array.isArray(data?.results) ? data.results : [];
      } catch (e) {
        console.error("load run result preview error:", e);
        this.runResultStats = null;
        this.runResultRows = [];
      } finally {
        this.loadingRunResult = false;
      }
    },
    formatRunCaseId(row) {
      if (row?.runCaseNumber) return `TCR-${String(row.runCaseNumber).padStart(3, "0")}`;
      return `TC-${String(row?.testCaseNumber || row?.testCaseId || 0).padStart(3, "0")}`;
    },
    displayRunResult(result) {
      const map = {
        Fail: "Failed",
        Failed: "Failed",
        Pass: "Passed",
        "Pass with Condition": "Pass with Condition",
        Blocked: "Blocked",
        "No Run": "No Run",
        "Not Run": "Not Run",
        "In Progress": "In progress",
        "In progress": "In progress",
      };
      const raw = String(result || "").trim();
      return map[raw] || raw || "No Run";
    },
    viewResultColor(status) {
      const map = {
        "No Run": "#adb5bd",
        "Not Run": "#000000",
        Fail: "#FF0000",
        Failed: "#FF0000",
        Pass: "#006400",
        Passed: "#006400",
        "Pass with Condition": "#006400",
        Blocked: "#fb6340",
        "In progress": "#000080",
        "In Progress": "#000080",
      };
      return map[String(status || "").trim()] || "#666699";
    },
    enumLabel(value) {
      const raw = String(value || "").trim();
      if (!raw) return "—";
      return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    viewActualIsByStep(tc) {
      return isByStepMode(tc?.actualResultMode);
    },
    viewActualText(tc, idx) {
      if (this.viewActualIsByStep(tc)) {
        const step = (tc?.steps || [])[idx];
        return String(step?.actualResult || "").trim() || "—";
      }
      return String(tc?.actualResult || "").trim() || "—";
    },
    viewOverviewActual(tc) {
      const text = String(tc?.actualResult || "").trim();
      if (text) return text;
      return (tc?.steps || []).map((s) => String(s?.actualResult || "").trim()).filter(Boolean).join("\n");
    },
    groupRunViewDetails(details) {
      const rows = Array.isArray(details) ? details : [];
      const tcMap = {};
      rows.forEach((d) => {
        const key = d.testCaseId ? String(d.testCaseId) : `${d.scenario}|${d.title}`;
        if (!tcMap[key]) {
          const status = String(d.testCaseStatus || "").trim();
          tcMap[key] = {
            key,
            testCaseId: d.testCaseId,
            tcLabel: this.formatRunCaseId(d),
            function: d.function || "",
            scenario: d.scenario || "",
            subScenario: d.subScenario || "",
            title: d.title ? String(d.title).trim() : "",
            testType: d.testType ? String(d.testType).trim() : "",
            priority: d.priority || "",
            preconditions: d.preconditions || "",
            testData: d.testData || "",
            runStatus: status === "Not Run" || !status ? "No Run" : status,
            actualResultMode: d.actualMode || "overview",
            actualResult: d.overviewActualDetail || "",
            steps: [],
          };
        }
        if (d.overviewActualDetail && !tcMap[key].actualResult) {
          tcMap[key].actualResult = d.overviewActualDetail;
        }
        if (d.actualMode) tcMap[key].actualResultMode = d.actualMode;
        if (d.stepNo != null) {
          tcMap[key].steps.push({
            stepNo: d.stepNo,
            platform: d.platform ? String(d.platform).trim() : "",
            stepDetail: d.stepDetail || "",
            expectedResult: d.expectedResult || "",
            actualResult: d.stepActualDetail || "",
          });
        }
      });
      Object.values(tcMap).forEach((tc) => {
        tc.steps.sort((a, b) => (a.stepNo || 0) - (b.stepNo || 0));
      });
      return Object.values(tcMap);
    },
    async loadRunDetailPreview() {
      if (!this.selectedRunId || this.selectedReportType !== "test_run_result_detail") {
        this.runDetailCases = [];
        return;
      }
      this.loadingRunDetail = true;
      try {
        const details = await testRunDetailsApi.getByRun(this.selectedRunId);
        this.runDetailCases = this.groupRunViewDetails(details);
      } catch (e) {
        console.error("load run detail preview error:", e);
        this.runDetailCases = [];
      } finally {
        this.loadingRunDetail = false;
      }
    },
    parseBulletLines(text) {
      const raw = String(text || "").trim();
      if (!raw) return [];
      return raw
        .split(/\n+/)
        .map((line) => line.replace(/^[\s•●○\-\u2022*]+/, "").trim())
        .filter(Boolean);
    },
    formatReportPdfDate(isoDate) {
      const raw = String(isoDate || "").trim();
      if (!raw) return "";
      const d = new Date(`${raw}T00:00:00`);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    },
    measureImageDataUrl(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => reject(new Error("image load failed"));
        img.src = dataUrl;
      });
    },
    async loadPdfLogoAsset() {
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/config/test-report-logo`);
        if (!res.ok) return null;
        const data = await res.json();
        if (!data?.dataUrl) return null;
        return { dataUrl: data.dataUrl, format: data.format || "PNG" };
      } catch (e) {
        console.warn("loadPdfLogoAsset:", e);
        return null;
      }
    },
    arrayBufferToBase64(buffer) {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
      }
      return btoa(binary);
    },
    async ensureK2dPdfFonts(doc) {
      if (!k2dFontVfsCache) {
        const [regRes, boldRes] = await Promise.all([
          fetch("/fonts/K2D-Regular.ttf"),
          fetch("/fonts/K2D-Bold.ttf"),
        ]);
        if (!regRes.ok || !boldRes.ok) {
          throw new Error("K2D font not found in /public/fonts");
        }
        k2dFontVfsCache = {
          regular: this.arrayBufferToBase64(await regRes.arrayBuffer()),
          bold: this.arrayBufferToBase64(await boldRes.arrayBuffer()),
        };
      }
      doc.addFileToVFS("K2D-Regular.ttf", k2dFontVfsCache.regular);
      doc.addFileToVFS("K2D-Bold.ttf", k2dFontVfsCache.bold);
      doc.addFont("K2D-Regular.ttf", PDF_FONT_FAMILY, "normal");
      doc.addFont("K2D-Bold.ttf", PDF_FONT_FAMILY, "bold");
    },
    async ensureK2dCanvasFonts() {
      if (!k2dCanvasFontsReady) {
        k2dCanvasFontsReady = (async () => {
          const [reg, bold] = await Promise.all([
            new FontFace(PDF_FONT_FAMILY, "url(/fonts/K2D-Regular.ttf)", { weight: "normal" }),
            new FontFace(PDF_FONT_FAMILY, "url(/fonts/K2D-Bold.ttf)", { weight: "bold" }),
          ]);
          await Promise.all([reg.load(), bold.load()]);
          document.fonts.add(reg);
          document.fonts.add(bold);
        })();
      }
      await k2dCanvasFontsReady;
    },
    deliverableOwnerName(userId) {
      const u = this.users.find((row) => Number(row.userId) === Number(userId));
      return u ? this.formatDeliverableName(u) : "";
    },
    async generateOverallQuality(language = "th") {
      if (!this.selectedRunId) return;
      this.generatingOverallQuality = true;
      this.overallQualityLang = language;
      this.overallQualityAiError = "";
      try {
        const data = await aiApi.generateOverallQuality({
          testRunId: this.selectedRunId,
          language,
          scopeTabText: this.subProjectScopeText || "",
        });
        if (data.overallQuality) this.reportForm.overallQuality = data.overallQuality;
        if (data.overallQualityStatus) {
          this.reportForm.overallQualityStatus = data.overallQualityStatus;
        }
        if (data.scopeOfTesting !== undefined) {
          this.reportForm.scopeOfTesting = data.scopeOfTesting;
        }
        if (data.scenarioConclusions && typeof data.scenarioConclusions === "object") {
          this.runScenarios.forEach((s, idx) => {
            const key = this.scenarioKey(s, idx);
            const name = s.name || "";
            const text = data.scenarioConclusions[name] || data.scenarioConclusions[key];
            if (text) this.scenarioConclusions[key] = text;
          });
        }
        if (data.criticalHighBugs !== undefined) {
          this.reportForm.criticalHighBugs = data.criticalHighBugs;
        }
        if (data.otherIssues !== undefined) {
          this.reportForm.otherIssues = data.otherIssues;
        }
        this.$nextTick(() => this.resizeOverallQualityTextarea());
      } catch (e) {
        console.error("generateOverallQuality error:", e);
        this.overallQualityAiError = e.message || "AI generation failed";
      } finally {
        this.generatingOverallQuality = false;
        this.overallQualityLang = "";
      }
    },
    async downloadPdf() {
      if (!this.selectedRunId || !this.selectedReportType) return;
      this.downloadingPdf = true;
      try {
        await this.loadNextReportNumber();
        if (
          this.selectedReportType === "test_run_result" ||
          this.selectedReportType === "test_report_with_test_case"
        ) {
          await this.loadRunResultPreview();
        }
        if (this.selectedReportType === "test_run_result_detail") {
          await Promise.all([this.loadRunResultPreview(), this.loadRunDetailPreview()]);
        }
        const { blob, fileName } = await this.buildCurrentReportPdf();
        let downloadName = fileName;
        try {
          const saved = await this.saveGeneratedReport(blob, fileName);
          if (saved?.reportNumber) this.reportForm.reportNumber = saved.reportNumber;
          downloadName = saved?.fileName || `${saved?.reportNumber || this.reportForm.reportNumber}.pdf`;
        } catch (saveErr) {
          console.error("save generated report error:", saveErr);
          window.alert("PDF downloaded, but failed to save to History. Please try again later.");
        }
        this.triggerBlobDownload(blob, downloadName);
        await this.loadNextReportNumber();
      } catch (e) {
        console.error("download test report pdf error:", e);
        window.alert(e?.message || "Failed to generate PDF. Please try again.");
      } finally {
        this.downloadingPdf = false;
      }
    },
    async buildCurrentReportPdf() {
      if (this.isEditableReportType) {
        return this.buildCustomTestReportPdfBlob({
          includeTestCaseResults: this.selectedReportType === "test_report_with_test_case",
        });
      }
      if (
        this.selectedReportType === "test_run_result" ||
        this.selectedReportType === "test_run_result_detail"
      ) {
        return this.buildRunResultStylePdfBlob(this.selectedReportType);
      }
      throw new Error("This report type cannot be generated yet.");
    },
    hexToRgb(hex) {
      const m = String(hex || "").replace("#", "");
      if (m.length !== 6) return [102, 102, 153];
      return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
    },
    async buildRunResultStylePdfBlob(kind) {
      const [jsPDFModule] = await Promise.all([import("jspdf")]);
      const { jsPDF } = jsPDFModule;
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      await Promise.all([this.ensureK2dPdfFonts(doc), this.ensureK2dCanvasFonts()]);
      const pdfFont = PDF_FONT_FAMILY;
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 16;
      const left = margin;
      const right = pageW - margin;
      const contentW = right - left;
      const lineH = 5;
      let y = margin;
      const ensureSpace = (needed) => {
        if (y + needed <= pageH - margin) return;
        doc.addPage();
        y = margin;
      };
      const pdfCanvasScale = 3;
      const pdfPtToPx = (pt) => pt * (96 / 72);
      const pdfMmToPx = (mm) => (mm * 96) / 25.4;
      const pdfPxToMm = (px) => (px * 25.4) / 96;
      const pdfHasThai = (s) => /[\u0E00-\u0E7F]/.test(String(s || ""));
      const pdfWrapThaiText = (text, maxWMm, fontSizePt, bold) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const fontPx = pdfPtToPx(fontSizePt) * pdfCanvasScale;
        const maxPx = pdfMmToPx(maxWMm) * pdfCanvasScale;
        ctx.font = `${bold ? "bold" : "normal"} ${fontPx}px ${PDF_FONT_FAMILY}`;
        const lines = [];
        let line = "";
        for (const ch of String(text || "")) {
          const trial = line + ch;
          if (ctx.measureText(trial).width > maxPx && line) {
            lines.push(line);
            line = ch;
          } else {
            line = trial;
          }
        }
        if (line) lines.push(line);
        return lines.length ? lines : [""];
      };
      const splitText = (text, maxW, fontSizePt = 9, bold = false) => {
        const s = String(text || "");
        if (!pdfHasThai(s)) return doc.splitTextToSize(s, maxW);
        return pdfWrapThaiText(s, maxW, fontSizePt, bold);
      };
      const drawText = (text, x, lineY, maxW, fontSizePt = 9, bold = false) => {
        const s = String(text || "");
        if (!s) return;
        doc.setFont(pdfFont, bold ? "bold" : "normal");
        doc.setFontSize(fontSizePt);
        if (!pdfHasThai(s)) {
          doc.text(s, x, lineY);
          return;
        }
        const fontPx = pdfPtToPx(fontSizePt) * pdfCanvasScale;
        const measureCanvas = document.createElement("canvas");
        const measureCtx = measureCanvas.getContext("2d");
        measureCtx.font = `${bold ? "bold" : "normal"} ${fontPx}px ${PDF_FONT_FAMILY}`;
        const metrics = measureCtx.measureText(s);
        const ascent = metrics.actualBoundingBoxAscent ?? fontPx * 0.85;
        const descent = metrics.actualBoundingBoxDescent ?? fontPx * 0.2;
        const w = Math.max(1, Math.ceil(metrics.width) + 2);
        const h = Math.max(1, Math.ceil(2 + ascent + descent));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.font = `${bold ? "bold" : "normal"} ${fontPx}px ${PDF_FONT_FAMILY}`;
        ctx.fillStyle = "#344767";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(s, 1, 1 + ascent);
        doc.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          x,
          lineY - pdfPxToMm((1 + ascent) / pdfCanvasScale),
          pdfPxToMm(w / pdfCanvasScale),
          pdfPxToMm(h / pdfCanvasScale),
        );
      };

      const isDetail = kind === "test_run_result_detail";
      const title = isDetail ? "TEST RUN RESULT (DETAILS)" : "TEST RUN RESULT (STATUS)";
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(13);
      doc.setTextColor(52, 71, 103);
      doc.text(`${title} : ${(this.reportDisplayProject === "—" ? "SUB PROJECT" : this.reportDisplayProject).toUpperCase()}`, left, y);
      y += 6;
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(131, 146, 171);
      doc.text(this.selectedRunName || "—", left, y);
      doc.setTextColor(52, 71, 103);
      y += 8;

      const info = [
        ["Sub Project", this.reportDisplayProject],
        ["Version", this.reportForm.version || "—"],
        ["Date", this.reportDisplayDate],
        ["Report Number", this.reportForm.reportNumber || "—"],
      ];
      const boxW = (contentW - 9) / 4;
      info.forEach((item, idx) => {
        const x = left + idx * (boxW + 3);
        doc.setDrawColor(233, 236, 239);
        doc.setFillColor(250, 251, 252);
        doc.roundedRect(x, y, boxW, 14, 1.2, 1.2, "FD");
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(7);
        doc.setTextColor(131, 146, 171);
        doc.text(item[0], x + 3, y + 5);
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(9);
        doc.setTextColor(52, 71, 103);
        drawText(item[1], x + 3, y + 11, boxW - 6, 9, true);
      });
      y += 20;

      const qaNames = this.selectedQaOwners.map((u) => this.formatUserName(u)).join(", ") || "—";
      const meta = [
        ["QA Owner", qaNames],
        ["Date", this.reportDisplayDate],
        ["Version", this.reportForm.version || "—"],
        ["Project Manager", this.reportForm.projectManager || "—"],
      ];
      meta.forEach(([label, value]) => {
        ensureSpace(7);
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(8);
        doc.setTextColor(131, 146, 171);
        doc.text(`${label}:`, left, y);
        doc.setTextColor(52, 71, 103);
        drawText(value, left + 38, y, contentW - 38, 9);
        y += 6;
      });

      y += 2;
      ensureSpace(16);
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(8);
      doc.setTextColor(131, 146, 171);
      doc.text("Status", left, y);
      doc.text("Overall Quality", left + 42, y);
      y += 5;
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(10);
      doc.setTextColor(52, 71, 103);
      doc.text(this.reportForm.overallQualityStatus || "—", left, y);
      const qualityLines = splitText(this.reportForm.overallQuality || "—", contentW - 42, 9);
      qualityLines.forEach((line, idx) => {
        if (idx > 0) ensureSpace(lineH);
        drawText(line, left + 42, y + idx * lineH, contentW - 42, 9);
      });
      y += Math.max(8, qualityLines.length * lineH + 4);

      const stats = this.runResultStats || { total: 0, passed: 0, failed: 0, noRun: 0, notRun: 0 };
      const statItems = [
        ["TOTAL", stats.total ?? 0, [52, 71, 103]],
        ["PASSED", stats.passed ?? 0, this.hexToRgb(this.viewResultColor("Pass"))],
        ["FAILED", stats.failed ?? 0, this.hexToRgb(this.viewResultColor("Fail"))],
        ["NO RUN", stats.noRun ?? 0, this.hexToRgb(this.viewResultColor("No Run"))],
        ["NOT RUN", stats.notRun ?? 0, this.hexToRgb(this.viewResultColor("Not Run"))],
      ];
      const statGap = 2.5;
      const statBoxW = (contentW - statGap * 4) / 5;
      ensureSpace(18);
      statItems.forEach((item, idx) => {
        const x = left + idx * (statBoxW + statGap);
        doc.setDrawColor(236, 239, 243);
        doc.setFillColor(250, 251, 252);
        doc.roundedRect(x, y, statBoxW, 15, 1.2, 1.2, "FD");
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(7);
        doc.setTextColor(131, 146, 171);
        doc.text(item[0], x + statBoxW / 2, y + 5, { align: "center" });
        doc.setFontSize(11);
        doc.setTextColor(...item[2]);
        doc.text(String(item[1]), x + statBoxW / 2, y + 11.5, { align: "center" });
      });
      y += 20;
      doc.setTextColor(52, 71, 103);

      if (!isDetail) {
        const colIdW = 22;
        const colStatusW = 28;
        const colTitleW = contentW - colIdW - colStatusW;
        const headerH = 7;
        const drawHeader = () => {
          ensureSpace(headerH + 6);
          doc.setFillColor(248, 249, 250);
          doc.rect(left, y, contentW, headerH, "F");
          doc.setFont(pdfFont, "bold");
          doc.setFontSize(8);
          doc.setTextColor(131, 146, 171);
          doc.text("ID", left + 3, y + 4.8);
          doc.text("TEST CASE", left + colIdW + 3, y + 4.8);
          doc.text("STATUS", left + colIdW + colTitleW + colStatusW / 2, y + 4.8, { align: "center" });
          y += headerH + 2;
        };
        drawHeader();
        (this.runResultRows || []).forEach((row) => {
          const idText = this.formatRunCaseId(row);
          const titleLines = splitText(row.title || "—", colTitleW - 6, 9);
          const rh = Math.max(6, titleLines.length * 4.2 + 2);
          if (y + rh > pageH - margin) {
            doc.addPage();
            y = margin;
            drawHeader();
          }
          doc.setFont(pdfFont, "normal");
          doc.setFontSize(9);
          doc.setTextColor(52, 71, 103);
          doc.text(idText, left + 3, y + 4);
          titleLines.forEach((line, li) => drawText(line, left + colIdW + 3, y + 4 + li * 4.2, colTitleW - 6, 9));
          const statusLabel = this.displayRunResult(row.status);
          doc.setFont(pdfFont, "bold");
          doc.setTextColor(...this.hexToRgb(this.viewResultColor(row.status)));
          doc.text(statusLabel, left + colIdW + colTitleW + colStatusW / 2, y + 4, { align: "center" });
          y += rh;
        });
      } else {
        (this.runDetailCases || []).forEach((tc) => {
          const titleLines = splitText(tc.title || "Untitled", contentW - 40, 10, true);
          const blockH = 18 + titleLines.length * 5 + (tc.steps || []).length * 8;
          ensureSpace(Math.min(blockH, 40));
          doc.setDrawColor(236, 239, 243);
          doc.setLineWidth(0.3);
          doc.line(left, y, right, y);
          y += 6;
          doc.setFont(pdfFont, "bold");
          doc.setFontSize(9);
          doc.setTextColor(52, 71, 103);
          doc.text(tc.tcLabel || "TC", left, y);
          const statusLabel = this.displayRunResult(tc.runStatus);
          doc.setTextColor(...this.hexToRgb(this.viewResultColor(tc.runStatus)));
          doc.text(statusLabel, right, y, { align: "right" });
          y += 5;
          doc.setTextColor(52, 71, 103);
          titleLines.forEach((line) => {
            ensureSpace(5);
            drawText(line, left, y, contentW - 4, 10, true);
            y += 5;
          });
          doc.setFont(pdfFont, "normal");
          doc.setFontSize(8);
          drawText(`Function: ${tc.function || "—"}  |  Scenario: ${tc.scenario || "—"}  |  Sub-Scenario: ${tc.subScenario || "—"}`, left, y, contentW, 8);
          y += 6;
          (tc.steps || []).forEach((step, idx) => {
            const stepLines = splitText(`${step.stepNo || idx + 1}. ${step.stepDetail || "—"}`, contentW - 8, 8);
            stepLines.forEach((line) => {
              ensureSpace(4.5);
              drawText(line, left + 2, y, contentW - 8, 8);
              y += 4.5;
            });
          });
          y += 3;
        });
      }

      y += 10;
      ensureSpace(28);
      const pmName = String(this.reportForm.projectManager || "").trim();
      const sigSlots = [
        ...this.selectedQaOwners.map((u) => ({ name: this.formatDeliverableName(u), label: "Quality Assurance" })),
        pmName ? { name: pmName, label: "Product Manager" } : null,
      ].filter(Boolean);
      sigSlots.slice(0, 4).forEach((slot, idx) => {
        const col = idx % 2;
        if (col === 0 && idx > 0) y += 24;
        ensureSpace(24);
        const cx = left + (col === 0 ? contentW * 0.25 : contentW * 0.75);
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.line(cx - 22, y, cx + 22, y);
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(9);
        doc.setTextColor(52, 71, 103);
        doc.text(`( ${slot.name} )`, cx, y + 6, { align: "center" });
        doc.setFont(pdfFont, "bold");
        doc.text(slot.label, cx, y + 12, { align: "center" });
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(`TestOps Hub: ${this.reportDisplayProject}`, left, pageH - 8);
        doc.text(`Page ${p} of ${totalPages}`, right, pageH - 8, { align: "right" });
      }
      const safeProject = (this.reportForm.projectName || "Test_Report").replace(/\s+/g, "_");
      const suffix = isDetail ? "_details" : "_status";
      const fileName = `${safeProject}_${this.reportForm.reportNumber || "report"}${suffix}.pdf`;
      return { blob: doc.output("blob"), fileName };
    },
    async buildCustomTestReportPdfBlob({ includeTestCaseResults = false } = {}) {
      const [jsPDFModule, logoAsset] = await Promise.all([import("jspdf"), this.loadPdfLogoAsset()]);
      const { jsPDF } = jsPDFModule;
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      await Promise.all([this.ensureK2dPdfFonts(doc), this.ensureK2dCanvasFonts()]);
      const pdfFont = PDF_FONT_FAMILY;

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 20;
      const left = margin;
      const right = pageW - margin;
      const contentW = right - left;
      const valueX = left + 32;
      const rowGap = 8;
      const lineH = 4.5;
      const lineHThai = 6;
      const lineHTable = 4.2;
      const bulletGap = 5.8;
      const fontBody = 10;
      const fontSection = 10;
      const fontTable = 10;
      const fontTitle = 20;
      const fontSig = 10;
      let y = margin;

      const ensureSpace = (needed) => {
        if (y + needed <= pageH - margin) return;
        doc.addPage();
        y = margin;
      };

      const pdfCanvasScale = 3;
      const pdfPtToPx = (pt) => pt * (96 / 72);
      const pdfMmToPx = (mm) => (mm * 96) / 25.4;
      const pdfPxToMm = (px) => (px * 25.4) / 96;
      const pdfHasThai = (s) => /[\u0E00-\u0E7F]/.test(String(s || ""));

      const pdfWrapThaiText = (text, maxWMm, fontSizePt, bold) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const fontPx = pdfPtToPx(fontSizePt) * pdfCanvasScale;
        const maxPx = pdfMmToPx(maxWMm) * pdfCanvasScale;
        ctx.font = `${bold ? "bold" : "normal"} ${fontPx}px ${PDF_FONT_FAMILY}`;
        const lines = [];
        let line = "";
        for (const ch of String(text || "")) {
          const trial = line + ch;
          if (ctx.measureText(trial).width > maxPx && line) {
            lines.push(line);
            line = ch;
          } else {
            line = trial;
          }
        }
        if (line) lines.push(line);
        return lines;
      };

      const pdfSplitTextToSize = (text, maxW, fontSizePt, bold = false) => {
        const s = String(text || "");
        if (!pdfHasThai(s)) return doc.splitTextToSize(s, maxW);
        return pdfWrapThaiText(s, maxW, fontSizePt, bold);
      };

      const pdfDrawTextLine = (line, x, lineY, maxWMm, fontSizePt, bold) => {
        if (!pdfHasThai(line)) {
          doc.text(line, x, lineY);
          return;
        }
        const fontPx = pdfPtToPx(fontSizePt) * pdfCanvasScale;
        const maxPx = pdfMmToPx(maxWMm) * pdfCanvasScale;
        const padTop = 8;
        const padBottom = 4;
        const padLeft = 2;
        const measureCanvas = document.createElement("canvas");
        const measureCtx = measureCanvas.getContext("2d");
        measureCtx.font = `${bold ? "bold" : "normal"} ${fontPx}px ${PDF_FONT_FAMILY}`;
        const metrics = measureCtx.measureText(line);
        const ascent = metrics.actualBoundingBoxAscent ?? fontPx * 0.85;
        const descent = metrics.actualBoundingBoxDescent ?? fontPx * 0.25;
        const w = Math.min(Math.ceil(metrics.width) + padLeft * 2, Math.ceil(maxPx));
        const h = Math.ceil(padTop + ascent + descent + padBottom);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = w;
        canvas.height = h;
        ctx.font = `${bold ? "bold" : "normal"} ${fontPx}px ${PDF_FONT_FAMILY}`;
        ctx.fillStyle = "#000000";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(line, padLeft, padTop + ascent);
        const baselineFromTop = pdfPxToMm((padTop + ascent) / pdfCanvasScale);
        doc.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          x,
          lineY - baselineFromTop,
          pdfPxToMm(w / pdfCanvasScale),
          pdfPxToMm(h / pdfCanvasScale),
        );
      };

      const statusPdfColor = (status) => {
        const s = String(status || "").trim().toLowerCase().replace(/_/g, " ");
        if (s === "passed" || s === "pass") return [34, 139, 34];
        if (s === "failed" || s === "fail" || s === "block" || s === "blocked") return [220, 53, 69];
        if (s === "in-progress" || s === "in progress") return [13, 147, 209];
        return [0, 0, 0];
      };

      const drawLabelValueLine = (label, value, { valueBold = false, valueXOffset = valueX, valueColor = null } = {}) => {
        ensureSpace(rowGap + 2);
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(fontBody);
        doc.text(`${label}:`, left, y);
        doc.setFont(pdfFont, valueBold ? "bold" : "normal");
        doc.setFontSize(fontBody);
        if (valueColor) doc.setTextColor(...valueColor);
        doc.text(String(value || ""), valueXOffset, y);
        doc.setTextColor(0, 0, 0);
        y += rowGap;
      };

      const drawLabelValueWrapped = (label, value, { valueBold = false, maxW = right - valueX } = {}) => {
        ensureSpace(rowGap + 4);
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(fontBody);
        doc.text(`${label}:`, left, y);
        doc.setFont(pdfFont, valueBold ? "bold" : "normal");
        doc.setFontSize(fontBody);
        const lines = pdfSplitTextToSize(value, maxW, fontBody, valueBold);
        lines.forEach((line, idx) => {
          if (idx > 0) ensureSpace(lineHThai + 1);
          pdfDrawTextLine(line, valueX, y + idx * lineHThai, maxW, fontBody, valueBold);
        });
        y += Math.max(rowGap, lines.length * lineHThai + 2);
      };

      const drawSectionTitle = (title, { underline = true, size = fontSection, gapAfter = 6.2 } = {}) => {
        ensureSpace(gapAfter + 6);
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(size);
        doc.text(title, left, y);
        if (underline) {
          doc.setLineWidth(0.3);
          doc.line(left, y + 1.1, left + doc.getTextWidth(title) + 1, y + 1.1);
          y += 7.8;
        } else {
          y += gapAfter;
        }
      };

      const drawBulletDot = (cx, cy, { hollow = false } = {}) => {
        doc.setDrawColor(0, 0, 0);
        doc.setFillColor(0, 0, 0);
        doc.setLineWidth(0.25);
        const r = hollow ? 0.55 : 0.75;
        const dotY = cy - 1.1;
        if (hollow) doc.circle(cx, dotY, r);
        else doc.circle(cx, dotY, r, "F");
      };

      const drawBulletBlock = (items, { hollow = false, indent = 4, textIndent = 4 } = {}) => {
        const bulletX = left + indent;
        const textX = left + indent + textIndent;
        const maxW = contentW - indent - textIndent - 2;
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(fontBody);
        items.forEach((item) => {
          const clean = String(item || "")
            .replace(/^[\s•●○\-\u2022*]+/, "")
            .trim();
          if (!clean) return;
          const lines = pdfSplitTextToSize(clean, maxW, fontBody);
          lines.forEach((line, idx) => {
            ensureSpace(bulletGap + 1);
            if (idx === 0) drawBulletDot(bulletX, y, { hollow });
            pdfDrawTextLine(line, idx === 0 ? textX : textX + 2, y, maxW, fontBody);
            y += bulletGap;
          });
        });
      };

      // Header layout (locked) — แก้เฉพาะค่าคงที่ด้านล่าง
      const logoMm = 20;
      const logoTop = margin - 10;
      const titleGapBelowLogo = 15; // ระยะโลโก้ → Test Report
      const reportNumberGapBelowTitle = 14; // ระยะ Test Report → Report Number

      if (logoAsset?.dataUrl) {
        try {
          doc.addImage(logoAsset.dataUrl, logoAsset.format, left, logoTop, logoMm, logoMm);
        } catch (e) {
          console.warn("PDF logo render failed:", e);
        }
      }

      const titleLineY = logoTop + logoMm + titleGapBelowLogo;
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(fontTitle);
      doc.text("Test Report", pageW / 2, titleLineY, { align: "center" });

      const reportNumberY = titleLineY + reportNumberGapBelowTitle;
      const reportNumberValue = this.reportForm.reportNumber || "LN2026-xxxx";
      doc.setFontSize(fontBody);
      doc.setFont(pdfFont, "bold");
      const reportNumberLabel = "Report Number:";
      const reportNumberLabelW = doc.getTextWidth(`${reportNumberLabel} `);
      doc.setFont(pdfFont, "normal");
      const reportNumberValueW = doc.getTextWidth(reportNumberValue);
      const reportNumberStartX = right - reportNumberLabelW - reportNumberValueW;
      doc.setFont(pdfFont, "bold");
      doc.text(`${reportNumberLabel} `, reportNumberStartX, reportNumberY);
      doc.setFont(pdfFont, "normal");
      doc.text(reportNumberValue, reportNumberStartX + reportNumberLabelW, reportNumberY);

      y = reportNumberY + 10;

      drawLabelValueLine("Project Name", this.reportForm.projectName || "");
      drawLabelValueLine("Version", this.reportForm.version || "");
      drawLabelValueLine("Date", this.formatReportPdfDate(this.reportForm.reportDate));
      drawLabelValueLine(
        "QA Owner",
        this.selectedQaOwners.map((u) => this.formatUserName(u)).join(", "),
      );
      drawLabelValueLine("Project Manager", this.reportForm.projectManager || "");

      y += 5;
      drawSectionTitle("Executive Summary", { underline: true, gapAfter: 0 });
      drawLabelValueLine("Status", this.reportForm.overallQualityStatus || "", {
        valueBold: true,
        valueColor: statusPdfColor(this.reportForm.overallQualityStatus),
      });
      drawLabelValueWrapped("Overall Quality", this.reportForm.overallQuality || "");

      y += 4;
      drawSectionTitle("Scope of Testing:", { underline: false, gapAfter: 4 });
      y += lineH;
      const scopeText = String(this.reportForm.scopeOfTesting || "").trim();
      if (scopeText) {
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(fontBody);
        const scopeLines = pdfSplitTextToSize(scopeText, contentW, fontBody);
        scopeLines.forEach((line, idx) => {
          if (idx > 0) ensureSpace(lineHThai + 1);
          pdfDrawTextLine(line, left, y, contentW, fontBody);
          y += lineHThai;
        });
        y += 2;
      }

      const tableRows = this.runScenarios.length
        ? this.runScenarios.map((s, idx) => [
            s.name || "—",
            this.scenarioConclusions[this.scenarioKey(s, idx)] || "",
          ])
        : [["", ""]];
      const col1W = contentW * 0.40;
      const col2W = contentW - col1W;
      const cellPadX = 2.5;
      const cellPadY = 3;
      const tableHeaderH = 9;

      const measureCellLines = (text, maxW) => doc.splitTextToSize(String(text || ""), maxW);

      const rowHeights = tableRows.map(([c1, c2]) => {
        const h1 = measureCellLines(c1, col1W - cellPadX * 2).length;
        const h2 = measureCellLines(c2, col2W - cellPadX * 2).length;
        const lines = Math.max(h1, h2, 1);
        return Math.max(12, lines * lineHTable + cellPadY + 4);
      });

      let tableTop = y;
      let totalTableH = tableHeaderH + rowHeights.reduce((a, b) => a + b, 0);
      ensureSpace(totalTableH + 4);
      tableTop = y;

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.25);
      doc.rect(left, tableTop, contentW, totalTableH);
      doc.line(left + col1W, tableTop, left + col1W, tableTop + totalTableH);
      doc.line(left, tableTop + tableHeaderH, left + contentW, tableTop + tableHeaderH);

      let rowY = tableTop + tableHeaderH;
      rowHeights.forEach((rh, idx) => {
        if (idx > 0) doc.line(left, rowY, left + contentW, rowY);
        rowY += rh;
      });

      doc.setFont(pdfFont, "bold");
      doc.setFontSize(fontBody);
      doc.text("Function / Category", left + col1W / 2, tableTop + 6.2, { align: "center" });
      doc.text("Conclusion", left + col1W + col2W / 2, tableTop + 6.2, { align: "center" });

      doc.setFont(pdfFont, "normal");
      doc.setFontSize(fontTable);
      let bodyY = tableTop + tableHeaderH;
      tableRows.forEach(([c1, c2], idx) => {
        const rh = rowHeights[idx];
        const lines1 = measureCellLines(c1, col1W - cellPadX * 2);
        const lines2 = measureCellLines(c2, col2W - cellPadX * 2);
        const textY = bodyY + cellPadY + 3.5;
        lines1.forEach((line, li) => doc.text(line, left + cellPadX, textY + li * lineHTable));
        lines2.forEach((line, li) => doc.text(line, left + col1W + cellPadX, textY + li * lineHTable));
        bodyY += rh;
      });

      y = tableTop + totalTableH + 12;

      drawSectionTitle("Critical & High Priority Bugs", { underline: false });
      const bugText = String(this.reportForm.criticalHighBugs || "").trim();
      const bugList = this.parseBulletLines(bugText);
      drawBulletBlock(bugList.length ? bugList : bugText ? [bugText] : []);

      const otherText = String(this.reportForm.otherIssues || "").trim();
      y += 3.5;
      drawSectionTitle("Remark", { underline: false });
      if (otherText) {
        const otherList = this.parseBulletLines(otherText);
        drawBulletBlock(otherList.length ? otherList : [otherText]);
      }

      y += 3.5;
      drawSectionTitle("Artifacts & Evidence", { underline: false });
      const artifactText = String(this.reportForm.artifactsEvidence || "").trim();
      const artifactList = artifactText ? this.parseBulletLines(artifactText) : [];
      const artifactFallbackDash = () => {
        ensureSpace(bulletGap + 1);
        drawBulletDot(left + 4, y);
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(fontBody);
        doc.text("-", left + 8, y);
        y += bulletGap;
      };
      if (artifactList.length) {
        drawBulletBlock(artifactList);
      } else if (artifactText && artifactText !== "-") {
        drawBulletBlock([artifactText]);
      } else {
        artifactFallbackDash();
      }

      // Signature grid — keep entire block on one page with signature lines
      const sigRowH = 28;
      const qaSigSlots = this.selectedQaOwners.map((u) => ({
        name: this.formatDeliverableName(u),
        label: "Quality Assurance",
      }));
      const allSigSlots = [
        ...qaSigSlots,
        { name: "Sawitri Mahapornchai", label: "Senior Quality Assurance" },
        { name: "Montai Settapokin", label: "Head of Engineering" },
        { name: String(this.reportForm.projectManager || "").trim(), label: "Product Manager" },
      ].filter((slot) => String(slot.name || "").trim());
      const sigRows = [];
      for (let i = 0; i < allSigSlots.length; i += 2) {
        sigRows.push(allSigSlots.slice(i, i + 2));
      }
      const sigBlockH = sigRowH * Math.max(sigRows.length, 1) + 12;
      const sigLineHalf = 22;
      const sigLineWidth = 0.15;
      const sigDateText = "Date___________________";
      const sigTopClearance = 20;
      y += 25;
      if (y + sigTopClearance + sigBlockH > pageH - margin) {
        doc.addPage();
        y = margin;
      }
      y += sigTopClearance;
      const sigColW = contentW / 2;

      const drawSigRow = (rowSlots) => {
        const lineY = y;
        rowSlots.forEach((slot, col) => {
          const cx = left + col * sigColW + sigColW / 2;
          doc.setLineWidth(sigLineWidth);
          doc.line(cx - sigLineHalf, lineY, cx + sigLineHalf, lineY);
        });
        y = lineY + 7;
        rowSlots.forEach((slot, col) => {
          const cx = left + col * sigColW + sigColW / 2;
          if (slot.name) {
            doc.setFont(pdfFont, "normal");
            doc.setFontSize(fontSig);
            doc.text(`( ${slot.name} )`, cx, y, { align: "center" });
          }
        });
        y += 6.5;
        rowSlots.forEach((slot, col) => {
          const cx = left + col * sigColW + sigColW / 2;
          doc.setFont(pdfFont, "normal");
          doc.setFontSize(fontSig);
          doc.text(sigDateText, cx, y, { align: "center" });
        });
        y += 6.5;
        rowSlots.forEach((slot, col) => {
          const cx = left + col * sigColW + sigColW / 2;
          doc.setFont(pdfFont, "bold");
          doc.setFontSize(fontSig);
          doc.text(slot.label, cx, y, { align: "center" });
        });
        y += 20;
      };

      sigRows.forEach((row) => drawSigRow(row));

      if (includeTestCaseResults) {
        let tcStats = this.runResultStats ? { ...this.runResultStats } : null;
        let tcCaseList = Array.isArray(this.runResultRows) ? [...this.runResultRows] : [];

        if (!tcStats || !tcCaseList.length) {
          try {
            const reportData = await reportsApi.getTestReport(this.selectedRunId).catch(() => null);
            if (!tcStats && reportData?.stats) tcStats = reportData.stats;
            if (!tcCaseList.length && Array.isArray(reportData?.results)) {
              tcCaseList = reportData.results.map((r) => ({
                ...r,
                status: r.status || "Not Run",
              }));
            }
          } catch (e) {
            console.error("load test case data for pdf:", e);
          }
        }

        if (!tcStats && tcCaseList.length) {
          let passed = 0;
          let failed = 0;
          let noRun = 0;
          let notRun = 0;
          tcCaseList.forEach((row) => {
            const s = String(row.status || "").trim().toLowerCase();
            if (s === "pass" || s === "passed" || s === "pass with condition") passed += 1;
            else if (s === "fail" || s === "failed") failed += 1;
            else if (s === "not run") notRun += 1;
            else noRun += 1;
          });
          tcStats = { total: tcCaseList.length, passed, failed, noRun, notRun };
        }

        if (tcStats || tcCaseList.length) {
          if (!tcStats) {
            tcStats = { total: tcCaseList.length, passed: 0, failed: 0, noRun: 0, notRun: tcCaseList.length };
          }
          doc.addPage();
          y = margin;

          doc.setFont(pdfFont, "bold");
          doc.setFontSize(14);
          doc.setTextColor(52, 71, 103);
          doc.text("TEST CASES", left, y);
          doc.setTextColor(0, 0, 0);
          y += 10;

          const tcPalette = {
            heading: [52, 71, 103],
            label: [131, 146, 171],
            body: [52, 71, 103],
            border: [233, 236, 239],
            headerBg: [248, 249, 250],
            cardBg: [250, 251, 252],
            cardBorder: [236, 239, 243],
            rowBg: [248, 249, 250],
            passValue: [40, 167, 69],
            failValue: [231, 76, 60],
          };

          const hexToRgb = (hex) => {
            const m = String(hex || "").replace("#", "");
            if (m.length !== 6) return [102, 102, 153];
            return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
          };
          const statGap = 2.5;
          const statBoxCount = 5;
          const statBoxW = (contentW - statGap * (statBoxCount - 1)) / statBoxCount;
          const statBoxH = 15;
          ensureSpace(statBoxH + 10);
          const statTop = y;
          const statItems = [
            {
              label: "TOTAL",
              value: tcStats.total ?? 0,
              valueColor: tcPalette.heading,
            },
            {
              label: "PASSED",
              value: tcStats.passed ?? 0,
              valueColor: hexToRgb(this.viewResultColor("Pass")),
            },
            {
              label: "FAILED",
              value: tcStats.failed ?? 0,
              valueColor: hexToRgb(this.viewResultColor("Fail")),
            },
            {
              label: "NO RUN",
              value: tcStats.noRun ?? 0,
              valueColor: hexToRgb(this.viewResultColor("No Run")),
            },
            {
              label: "NOT RUN",
              value: tcStats.notRun ?? 0,
              valueColor: hexToRgb(this.viewResultColor("Not Run")),
            },
          ];
          statItems.forEach((item, idx) => {
            const bx = left + idx * (statBoxW + statGap);
            doc.setDrawColor(...tcPalette.cardBorder);
            doc.setFillColor(...tcPalette.cardBg);
            doc.setLineWidth(0.25);
            doc.roundedRect(bx, statTop, statBoxW, statBoxH, 1.5, 1.5, "FD");
            doc.setFont(pdfFont, "bold");
            doc.setFontSize(7);
            doc.setTextColor(...tcPalette.label);
            doc.text(item.label, bx + statBoxW / 2, statTop + 5, { align: "center" });
            doc.setFont(pdfFont, "bold");
            doc.setFontSize(11);
            doc.setTextColor(...item.valueColor);
            doc.text(String(item.value), bx + statBoxW / 2, statTop + 11.5, { align: "center" });
          });
          doc.setTextColor(0, 0, 0);
          y = statTop + statBoxH + 10;

          const tcColIdW = 22;
          const tcColStatusW = 28;
          const tcColTitleW = contentW - tcColIdW - tcColStatusW;
          const tcFontSize = 9;
          const tcTextLineH = tcFontSize * 0.42;
          const tcPadTop = 1.2;
          const tcPadBottom = 0.8;
          const tcCellPadX = 3;
          const tcRowGap = 2.8;
          const tcHeaderH = tcPadTop + tcPadBottom + tcTextLineH;
          const tcBodyColor = tcPalette.body;

          const formatTcId = (row) => this.formatRunCaseId(row);

          const tcBaselineAt = (rowTop, lineIndex = 0) => rowTop + tcPadTop + tcTextLineH * 0.72 + lineIndex * tcTextLineH;

          const tcRowHeightForLineCount = (lineCount) =>
            tcPadTop + tcPadBottom + Math.max(lineCount, 1) * tcTextLineH;

          const pdfSplitTextToSizeTable = (text, maxW) => {
            const s = String(text || "").trim();
            if (!s) return [""];
            if (!pdfHasThai(s)) return doc.splitTextToSize(s, maxW);

            const measureCanvas = document.createElement("canvas");
            const ctx = measureCanvas.getContext("2d");
            const fontPx = pdfPtToPx(tcFontSize) * pdfCanvasScale;
            const maxPx = pdfMmToPx(maxW) * pdfCanvasScale;
            ctx.font = `normal ${fontPx}px ${PDF_FONT_FAMILY}`;

            const charWrap = (segment) => {
              const out = [];
              let line = "";
              for (const ch of segment) {
                const trial = line + ch;
                if (ctx.measureText(trial).width > maxPx && line) {
                  out.push(line);
                  line = ch;
                } else {
                  line = trial;
                }
              }
              if (line) out.push(line);
              return out;
            };

            const lines = [];
            let current = "";
            for (const part of s.split(/(\s+)/)) {
              if (!part) continue;
              const trial = current + part;
              if (ctx.measureText(trial).width > maxPx && current.trim()) {
                charWrap(current).forEach((ln) => lines.push(ln));
                current = part.trimStart();
              } else {
                current = trial;
              }
            }
            if (current.trim()) {
              if (ctx.measureText(current).width > maxPx) charWrap(current).forEach((ln) => lines.push(ln));
              else lines.push(current);
            }
            return lines.length ? lines : [s];
          };

          const drawTcTableHeader = () => {
            ensureSpace(tcHeaderH + tcRowGap + 4);
            const top = y;
            const headerBottom = top + tcHeaderH + 1;
            doc.setFillColor(...tcPalette.headerBg);
            doc.rect(left, top, contentW, headerBottom - top, "F");
            doc.setFont(pdfFont, "bold");
            doc.setFontSize(tcFontSize);
            doc.setTextColor(...tcPalette.label);
            const headerTextY = tcBaselineAt(top);
            doc.text("ID", left + tcCellPadX, headerTextY);
            doc.text("TEST CASE", left + tcColIdW + tcCellPadX, headerTextY);
            doc.text("STATUS", left + tcColIdW + tcColTitleW + tcColStatusW / 2, headerTextY, { align: "center" });
            doc.setDrawColor(...tcPalette.border);
            doc.setLineWidth(0.2);
            doc.line(left, headerBottom, left + contentW, headerBottom);
            y = headerBottom + tcRowGap;
          };

          const drawTcCellTextLine = (line, x, lineY, maxWMm, bold = false) => {
            const text = String(line || "");
            if (!text.trim()) return;
            doc.setFont(pdfFont, bold ? "bold" : "normal");
            doc.setFontSize(tcFontSize);
            if (!pdfHasThai(text)) {
              doc.setTextColor(...tcBodyColor);
              doc.text(text, x, lineY);
              return;
            }
            const fontPx = pdfPtToPx(tcFontSize) * pdfCanvasScale;
            const maxPx = pdfMmToPx(maxWMm) * pdfCanvasScale;
            const padTop = 1;
            const padBottom = 0;
            const padLeftImg = 1;
            const fontStyle = bold ? "bold" : "normal";
            const measureCanvas = document.createElement("canvas");
            const measureCtx = measureCanvas.getContext("2d");
            measureCtx.font = `${fontStyle} ${fontPx}px ${PDF_FONT_FAMILY}`;
            const metrics = measureCtx.measureText(text);
            const ascent = metrics.actualBoundingBoxAscent ?? fontPx * 0.85;
            const descent = metrics.actualBoundingBoxDescent ?? fontPx * 0.2;
            const w = Math.max(1, Math.min(Math.ceil(metrics.width) + padLeftImg * 2, Math.ceil(maxPx)));
            const h = Math.max(1, Math.ceil(padTop + ascent + descent + padBottom));
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = w;
            canvas.height = h;
            ctx.font = `${fontStyle} ${fontPx}px ${PDF_FONT_FAMILY}`;
            ctx.fillStyle = "#344767";
            ctx.textBaseline = "alphabetic";
            ctx.fillText(text, padLeftImg, padTop + ascent);
            const baselineFromTop = pdfPxToMm((padTop + ascent) / pdfCanvasScale);
            const imgW = Math.max(0.1, pdfPxToMm(w / pdfCanvasScale));
            const imgH = Math.max(0.1, pdfPxToMm(h / pdfCanvasScale));
            doc.addImage(
              canvas.toDataURL("image/png"),
              "PNG",
              x,
              lineY - baselineFromTop,
              imgW,
              imgH,
            );
          };

          const drawTcDataRow = (row) => {
            const idText = formatTcId(row);
            const titleText = row.title || "—";
            const statusLabel = this.displayRunResult(row.status);
            const statusColor = hexToRgb(this.viewResultColor(row.status));
            const titleMaxW = tcColTitleW - tcCellPadX * 2;
            const titleLines = pdfSplitTextToSizeTable(titleText, titleMaxW).filter((ln) => String(ln || "").trim());
            const lineCount = Math.max(titleLines.length, 1);
            const rh = tcRowHeightForLineCount(lineCount);

            if (y + rh > pageH - margin) {
              doc.addPage();
              y = margin;
              drawTcTableHeader();
            }

            const rowTop = y;
            const textY0 = tcBaselineAt(rowTop);
            doc.setFont(pdfFont, "normal");
            doc.setFontSize(tcFontSize);
            doc.setTextColor(...tcBodyColor);
            doc.text(idText, left + tcCellPadX, textY0);
            titleLines.forEach((line, li) => {
              drawTcCellTextLine(
                line,
                left + tcColIdW + tcCellPadX,
                tcBaselineAt(rowTop, li),
                titleMaxW,
              );
            });
            const statusCenterX = left + tcColIdW + tcColTitleW + tcColStatusW / 2;
            doc.setFont(pdfFont, "bold");
            doc.setFontSize(tcFontSize);
            doc.setTextColor(...statusColor);
            doc.text(statusLabel, statusCenterX, textY0, { align: "center" });
            doc.setTextColor(...tcBodyColor);
            y = rowTop + rh + tcRowGap;
          };

          drawTcTableHeader();
          tcCaseList.forEach((row) => drawTcDataRow(row));
        }
      }

      const totalPages = doc.internal.getNumberOfPages();
      const footerSubProject = this.reportForm.projectName || "Sub Project";
      for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(`TestOps Hub: ${footerSubProject}`, left, pageH - 8);
        doc.text(`Page ${p} of ${totalPages}`, right, pageH - 8, { align: "right" });
        doc.setTextColor(0, 0, 0);
      }

      const safeProject = (this.reportForm.projectName || "Test_Report").replace(/\s+/g, "_");
      const reportSuffix = includeTestCaseResults ? "_with_test_case" : "";
      const fileName = `${safeProject}_${this.reportForm.reportNumber || "report"}${reportSuffix}.pdf`;
      return { blob: doc.output("blob"), fileName };
    },
    async downloadCustomTestReportPdf(options = {}) {
      const { blob, fileName } = await this.buildCustomTestReportPdfBlob(options);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.report-preview-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #8392ab;
}

.report-select-box {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  background: #fff;
  padding: 0.8rem;
}

.report-preview-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  background: #fff;
  padding: 1rem;
}

.pdf-like-header {
  border-bottom: 1px solid #eceff3;
  padding-bottom: 0.6rem;
}

.pdf-like-title {
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #344767;
}

.pdf-like-sub {
  font-size: 0.74rem;
  color: #8392ab;
}

.pdf-like-info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.pdf-like-info-grid--stats {
  display: flex;
  flex-wrap: nowrap;
  grid-template-columns: none;
}
.pdf-like-info-grid--stats .pdf-like-info-item {
  flex: 1 1 0;
  min-width: 0;
}

.pdf-like-info-item {
  border: 1px solid #eceff3;
  border-radius: 8px;
  background: #fafbfc;
  padding: 0.45rem 0.55rem;
}

.pdf-like-info-label {
  font-size: 0.64rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8b95a7;
  font-weight: 700;
}

.pdf-like-info-value {
  margin-top: 0.08rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
}

.pdf-like-section-title {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 800;
  color: #6f7b92;
}

.report-empty-message {
  min-height: 78px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.preview-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 0.35rem 0;
}

.preview-label {
  color: #8392ab;
  font-weight: 600;
}

.preview-value {
  color: #344767;
  font-weight: 700;
}

.qa-owner-multiselect { position: relative; }
.qa-owner-selector {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  background: #fff;
  font-size: 0.75rem;
}
.qa-owner-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef4ff;
  color: #344767;
  border: 1px solid #d5e3ff;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.72rem;
}
.qa-owner-chip-remove {
  cursor: pointer;
  font-weight: 700;
}
.qa-owner-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
  max-height: 210px;
  overflow-y: auto;
}
.qa-owner-dropdown-item {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  font-size: 0.78rem;
  cursor: pointer;
}
.qa-owner-dropdown-item:hover {
  background: #f8f9fa;
}

.status-quality-grid {
  display: grid;
  grid-template-columns: 15.5rem minmax(0, 1fr) auto auto;
  grid-template-rows: auto auto;
  gap: 0.35rem 0.5rem;
  align-items: start;
}
.sq-label-status {
  grid-column: 1;
  grid-row: 1;
  align-self: end;
}
.sq-label-quality {
  grid-column: 2;
  grid-row: 1;
  align-self: end;
}
.sq-actions {
  grid-column: 3 / 5;
  grid-row: 1;
  justify-self: end;
  align-self: end;
}
.sq-status {
  grid-column: 1;
  grid-row: 2;
  align-self: start;
  width: 15.5rem;
  max-width: 100%;
  font-size: 0.72rem;
}
.sq-quality-wrap {
  grid-column: 2 / 5;
  grid-row: 2;
  align-self: start;
  min-width: 0;
}
.sq-quality {
  resize: none;
  overflow: hidden;
  min-height: 56px;
}
.sq-gen-btn {
  font-size: 0.68rem;
  padding: 0.15rem 0.5rem;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .status-quality-grid {
    grid-template-columns: 1fr 1fr;
  }
  .sq-label-quality,
  .sq-quality-wrap {
    grid-column: 1 / -1;
  }
  .sq-actions {
    grid-column: 1 / -1;
    justify-self: start;
  }
  .sq-status {
    width: 100%;
  }
}

.bullet-textarea {
  white-space: pre-wrap;
  line-height: 1.45;
}

.scenario-list {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;
  background: #fff;
}
.scenario-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 0.5rem;
  align-items: start;
  padding: 0.35rem 0;
}
.scenario-row + .scenario-row {
  border-top: 1px solid #f1f3f5;
}
.scenario-name {
  font-size: 0.78rem;
  color: #344767;
  font-weight: 600;
  padding-top: 0.35rem;
}

@media (max-width: 992px) {
  .pdf-like-info-grid:not(.pdf-like-info-grid--stats) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.run-result-status {
  font-weight: 700;
}
.run-result-status strong {
  font-weight: 700;
}

.qa-tr-report-case {
  border: 1px solid #dcdcdc;
  border-radius: 10px;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
}
.qa-tr-report-case-top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
}
.qa-tr-report-case-id {
  font-size: 0.72rem;
  font-weight: 700;
  color: #8392ab;
}
.qa-tr-report-case-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #344767;
}
.qa-tr-report-type-row,
.qa-tr-report-hierarchy {
  font-size: 0.78rem;
  color: #344767;
}
.qa-tr-report-type-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.25rem;
}
.qa-tr-report-result-head {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}
.qa-tr-report-result-chip {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  border: 1px solid;
  white-space: nowrap;
  min-width: 120px;
  text-align: center;
}
.qa-tr-report-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin: 0.7rem 0;
}
.qa-tr-report-label {
  font-size: 0.68rem;
  font-weight: 800;
  color: #212529;
  text-transform: uppercase;
  margin-bottom: 0.2rem;
}
.qa-tr-report-steps {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.78rem;
}
.qa-tr-report-steps th,
.qa-tr-report-steps td {
  border: 1px solid #dcdcdc;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
  overflow-wrap: anywhere;
}
.qa-tr-report-steps th {
  background: #344767;
  color: #fff;
  font-weight: 700;
}
.qa-tr-report-col-no { width: 50px; }
.qa-tr-report-col-module { width: 80px; }
.qa-tr-report-col-step,
.qa-tr-report-col-expected,
.qa-tr-report-col-actual {
  width: calc((100% - 130px) / 3);
}
</style>
